import { IconButton, SelectInput, SelectInputItem, Tag, Tooltip } from '@ads/components-react';
import {
  BookOpenIcon,
  CalculatorIcon,
  CalendarDaysIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { EClassRoomDisciplinesStatus } from '@/shared/api/generated';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/shared/ui/atoms';
import { useGradeConfig } from '../../../hooks/useGradeConfig';
import { type Activity, EActivityType } from '../../../types/gradeConfig';
import { fmtBR } from '../../../utils/format';
import { getSource, sourceItem } from '../../../utils/gradeCalc';
import { TypeTag } from '../../atoms/TypeTag';
import { DeleteLinkConfirmModal } from '../../molecules/DeleteLinkConfirmModal';

/** Grid de colunas compartilhado entre cabeçalho e linhas da tabela de vínculos. */
export const ROW_GRID =
  'grid grid-cols-[2rem_max-content_minmax(12rem,1fr)_auto_2.5rem] items-center gap-nano';

/** Chave i18n do rótulo de status da aula (1 Planejado | 2 Finalizado | 3 Cancelado | 4 Confirmado). */
const LESSON_STATUS_LABEL: Record<EClassRoomDisciplinesStatus, string> = {
  1: 'linked.status.planned',
  2: 'linked.status.finished',
  3: 'linked.status.canceled',
  4: 'linked.status.confirmed',
};

/** Tom visual da `Tag` de status da aula. */
const LESSON_STATUS_TONE: Record<
  EClassRoomDisciplinesStatus,
  'default' | 'positive' | 'negative' | 'helper'
> = {
  1: 'helper',
  2: 'positive',
  3: 'negative',
  4: 'positive',
};

export interface LinkedDisciplineRowProps {
  readonly activity: Activity;
}

/**
 * Linha de vínculo na tabela de disciplinas: tipo, item, botões de controle do documento
 * (IconButton com Tooltip) e expansão das aulas/provas com pesos somente-leitura.
 */
export function LinkedDisciplineRow({ activity }: LinkedDisciplineRowProps) {
  const { t } = useTranslation('gradeAttendanceConfig');
  const { classGroup, expanded, toggleExpand, updateActivity, toggleControl, removeActivity } =
    useGradeConfig();
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (!classGroup) return null;

  const isDisc = activity.type === EActivityType.Discipline;
  const item = sourceItem(classGroup, activity);
  const disc = isDisc ? classGroup.disciplines.find(d => d.id === activity.refId) : undefined;
  const lessons = disc?.lessons ?? [];
  const canExpand = isDisc && lessons.length > 1;
  const isOpen = !!expanded[activity.id];

  const lessonsLabel =
    lessons.length === 1
      ? t('linked.aulasUnit', { count: 1 })
      : t('linked.aulasMulti', { count: lessons.length });
  const period = disc ? `${fmtBR(disc.startDate)} – ${fmtBR(disc.endDate)}` : '';

  const refOptions = getSource(classGroup, activity.type);

  return (
    <Accordion
      type="single"
      collapsible
      value={isOpen ? activity.id : ''}
      onValueChange={() => toggleExpand(activity.id)}
      className="border-b-hairline border-ctx-layout-border"
    >
      <AccordionItem value={activity.id}>
        <div className={`${ROW_GRID} px-mini py-nano`}>
          {/* Expand */}
          <div className="flex justify-center">
            {canExpand && (
              <AccordionTrigger
                title={t('linked.expandTitle')}
                aria-label={t('linked.expandTitle')}
                className="w-xxs h-xxs justify-center rounded-[0.375rem] border-hairline border-ctx-layout-border bg-ctx-layout-surface text-ctx-content-base"
              >
                <ChevronRightIcon className="size-micro transition-transform group-data-[state=open]:rotate-90" />
              </AccordionTrigger>
            )}
          </div>

          {/* Tipo */}
          <div>
            <TypeTag type={activity.type} />
          </div>

          {/* Item vinculado */}
          <div className="min-w-none">
            {isDisc ? (
              <div className="flex items-center gap-nano font-bold text-ctx-content-title ts-subtitle-xxs">
                <BookOpenIcon className="size-mini shrink-0 text-ctx-content-placeholder" />
                <span className="truncate">{item?.name ?? '—'}</span>
              </div>
            ) : (
              <SelectInput
                value={activity.refId ?? ''}
                onValueChange={value => updateActivity(activity.id, { refId: value || null })}
                aria-label={t('linked.columns.item')}
              >
                {refOptions.map(o => (
                  <SelectInputItem key={o.id} value={o.id}>
                    {o.name}
                  </SelectInputItem>
                ))}
              </SelectInput>
            )}
            {isDisc && (
              <div className="mt-quark flex flex-wrap items-center gap-quark text-ctx-content-placeholder ts-caption-xxs">
                <Tag
                  custom={{ text: 'text-nowrap' }}
                  size="sm"
                  label={lessonsLabel}
                  status="default"
                />
                <span className="tabular-nums">{period}</span>
              </div>
            )}
          </div>

          {/* Controles do documento */}
          <div className="min-w-none">
            <div className="flex flex-wrap items-center gap-quark">
              <Tooltip title={t('controls.doc')}>
                <IconButton
                  icon={DocumentTextIcon}
                  ariaLabel={t('controls.doc')}
                  hierarchy={activity.showInDocument ? 'primary' : 'tertiary'}
                  size="sm"
                  onClick={() => toggleControl(activity, 'document')}
                />
              </Tooltip>
              <Tooltip title={t('controls.media')}>
                <IconButton
                  icon={CalculatorIcon}
                  ariaLabel={t('controls.media')}
                  hierarchy={activity.countsInGrade ? 'primary' : 'tertiary'}
                  size="sm"
                  onClick={() => toggleControl(activity, 'grade')}
                />
              </Tooltip>
              <Tooltip title={t('controls.freq')}>
                <IconButton
                  icon={CalendarDaysIcon}
                  ariaLabel={t('controls.freq')}
                  hierarchy={activity.countsInAttendance ? 'primary' : 'tertiary'}
                  size="sm"
                  onClick={() => toggleControl(activity, 'attendance')}
                />
              </Tooltip>
            </div>
          </div>

          {/* Remover */}
          <div className="flex justify-end">
            <Tooltip title={t('linked.removeTitle')}>
              <IconButton
                icon={TrashIcon}
                ariaLabel={t('linked.removeTitle')}
                hierarchy="tertiary"
                size="sm"
                onClick={() => setConfirmOpen(true)}
              />
            </Tooltip>
          </div>
        </div>

        <DeleteLinkConfirmModal
          open={confirmOpen}
          itemName={item?.name ?? t(`types.${activity.type}.label`)}
          onOpenChange={setConfirmOpen}
          onConfirm={() => {
            removeActivity(activity.id);
            setConfirmOpen(false);
          }}
        />

        {/* Expansão: aulas e provas */}
        {canExpand && (
          <AccordionContent>
            <div className="border-t-hairline border-ctx-layout-border bg-ctx-layout-body px-mini py-mini pl-[2.75rem]">
              <div className="mb-nano">
                <span className="uppercase tracking-wide text-ctx-content-placeholder ts-caption-xxs">
                  {t('linked.expandedTitle')}
                </span>
              </div>
              <div className="flex flex-col gap-nano">
                {lessons.map(l => (
                  <div
                    key={l.id}
                    className="grid grid-cols-[1fr_auto] items-center gap-micro rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface px-micro py-nano"
                  >
                    <div className="flex min-w-none flex-col gap-quark">
                      <span className="flex items-center gap-nano">
                        <span className="truncate text-ctx-content-title ts-subtitle-xxs">
                          {l.name}
                        </span>
                        {l.status && (
                          <Tag
                            size="sm"
                            status={LESSON_STATUS_TONE[l.status]}
                            label={t(LESSON_STATUS_LABEL[l.status] as 'linked.status.planned')}
                            custom={{ text: 'text-nowrap' }}
                          />
                        )}
                      </span>
                      <span className="flex items-center gap-quark text-ctx-content-placeholder ts-caption-xxs">
                        <span className="tabular-nums">{fmtBR(l.date)}</span>
                        {l.exam.name && (
                          <>
                            <span aria-hidden="true">·</span>
                            <span className="truncate">{l.exam.name}</span>
                          </>
                        )}
                      </span>
                    </div>
                    <div className="flex items-center justify-center gap-quark">
                      <span className="text-ctx-content-placeholder ts-caption-xxs">
                        {t('linked.peso')}
                      </span>
                      <span className="tabular-nums text-ctx-content-title ts-subtitle-xxs pr-nano">
                        {l.exam.weight}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-nano text-ctx-content-placeholder ts-caption-xxs">
                {t('linked.expandedNote')}
              </p>
            </div>
          </AccordionContent>
        )}
      </AccordionItem>
    </Accordion>
  );
}
